import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";
import {
	PlanCard,
	PlanCta,
	PlanDescription,
	PlanLabelBadge,
	PlanPrice,
} from "./plan-modal-card-ui";

const MAILERLITE_EMBEDDED_FORM_ID = "70TsDW";
const MAILERLITE_ACCOUNT_ID = "2012782";

function MailerLiteForm() {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) return;

		// Create the form div imperatively so React never touches it
		const formDiv = document.createElement("div");
		formDiv.className = "ml-embedded";
		formDiv.setAttribute("data-form", MAILERLITE_EMBEDDED_FORM_ID);
		ref.current.appendChild(formDiv);

		// Load the universal script after the div is in the DOM
		const script = document.createElement("script");
		script.innerHTML = `
			(function(w,d,e,u,f,l,n){w[f]=w[f]||function(){(w[f].q=w[f].q||[])
			.push(arguments);},l=d.createElement(e),l.async=1,l.src=u,
			n=d.getElementsByTagName(e)[0],n.parentNode.insertBefore(l,n);})
			(window,document,'script','https://assets.mailerlite.com/js/universal.js','ml');
			ml('account', '${MAILERLITE_ACCOUNT_ID}');
		`;
		ref.current.appendChild(script);

		return () => {
			formDiv.remove();
			script.remove();
		};
	}, []);

	return <div ref={ref} />;
}

export function TeamPlanCard(props: {
	periodSelected: "monthly" | "annually";
}) {
	const { periodSelected } = props;

	const annualPrice = "$25";
	const annualFullPrice = "$49";
	const monthlyPrice = "$32";
	const monthlyFullPrice = "$59";

	const [opened, { open, close }] = useDisclosure(false);

	return (
		<>
			<Modal
				opened={opened}
				onClose={close}
				centered
				keepMounted
				title="Get early access to Team"
				zIndex={4000}
			>
				<MailerLiteForm />
			</Modal>

			<PlanCard active={false} comingSoon>
				<div className="opacity-50 select-none">
					<div className="flex items-center gap-2 mb-1 h-5">
						<PlanLabelBadge variant="enterprise">Team</PlanLabelBadge>
						<span className="text-[10px] uppercase font-bold tracking-widest border border-border-default text-text-muted px-2 py-0.5 rounded-full">
							Coming Soon
						</span>
					</div>

					<AnimatePresence mode="wait">
						{periodSelected === "annually" ? (
							<motion.div
								key="annual"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.15 }}
							>
								<PlanPrice
									price={annualPrice}
									fullPrice={annualFullPrice}
									period="monthly"
								/>
							</motion.div>
						) : (
							<motion.div
								key="monthly"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.15 }}
							>
								<PlanPrice
									price={monthlyPrice}
									fullPrice={monthlyFullPrice}
									period="monthly"
								/>
							</motion.div>
						)}
					</AnimatePresence>

					<PlanDescription>
						<p>
							Pro handles the work. Team handles the knowledge. When your team's
							SOPs, AI practices, and instructions live in one structured place,
							everyone works from the same foundation.
						</p>
					</PlanDescription>
				</div>

				<PlanCta>
					<button
						type="button"
						className="w-full rounded-xl h-10 bg-brand-primary hover:bg-brand-primary-hover text-text-inverse text-sm font-semibold transition-all flex items-center justify-center shadow-md shadow-(--ui-color-brand-primary)/10"
						onClick={open}
					>
						Get early access
					</button>
				</PlanCta>
			</PlanCard>
		</>
	);
}
